import React, { useCallback, useMemo, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useFocusEffect, useRoute } from '@react-navigation/native'
import { useApolloClient } from '@apollo/client'
import * as Sentry from '@sentry/react-native'
import {
  useMyCollaboratorsTimelineQuery,
  useCollaboratorsQuery,
  TimeLineAction,
  useUpdateTimelineReadUntilMutation,
  GetNewEventsCountDocument,
  MyCollaboratorsTimelineDocument,
} from '../../../../generated/graphql'
import {
  TimelineList,
  TimelineResultItem,
} from '../components/timeline-base.list'
import { timelineActionsAvailableForFilters } from '../timeline-excluded-event-types'
import { AppNavigatorParams } from '../../../../navigation/types'
import { useAuthContext } from '../../../../contexts/auth-context'

const TIMELINE_PAGE_SIZE = 20
const COLLABORATORS_PAGE_SIZE = 50

export const MyCollaboratorsUpdatesTab: React.FC = () => {
  const { t } = useTranslation(['nodes', 'common'])
  const { userId } = useAuthContext()
  const [updateTimelineReadUntil] = useUpdateTimelineReadUntilMutation()
  const client = useApolloClient()
  const route = useRoute()

  const routeParams = route.params as AppNavigatorParams['MyCollaborators']
  const initialNodeId = routeParams?.nodeId || null

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [selectedAction, setSelectedAction] = useState<TimeLineAction | null>(
    null
  )
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(
    initialNodeId
  )
  const [collaboratorSearch, setCollaboratorSearch] = useState('')

  const { data: collaboratorsData } = useCollaboratorsQuery({
    variables: {
      pagination: {
        offset: 0,
        limit: COLLABORATORS_PAGE_SIZE,
      },
      search: collaboratorSearch || undefined,
    },
  })

  const collaborators = collaboratorsData?.collaborators?.users ?? []
  const collaboratorItems = useMemo(() => {
    const allOption = { id: '', label: t('nodes:all') }
    const list = collaborators.map((c) => ({
      id: c.id,
      label: c.username || c.id,
    }))
    return [allOption, ...list]
  }, [collaborators, t])

  const actionsForQuery = useMemo((): TimeLineAction[] => {
    if (selectedAction != null) {
      return [selectedAction]
    }
    return timelineActionsAvailableForFilters()
  }, [selectedAction])

  const { data, loading, refetch, fetchMore } = useMyCollaboratorsTimelineQuery(
    {
      variables: {
        pagination: {
          offset: 0,
          limit: TIMELINE_PAGE_SIZE,
        },
        userId: selectedUserId || undefined,
        actions: actionsForQuery,
        nodeId: selectedNodeId || undefined,
      },
    }
  )

  useEffect(() => {
    const nodeId = routeParams?.nodeId || null
    setSelectedNodeId(nodeId)
  }, [routeParams?.nodeId])

  const results = (data?.myCollaboratorsEvents?.results ??
    []) as TimelineResultItem[]
  const total = data?.myCollaboratorsEvents?.total ?? 0

  useFocusEffect(
    useCallback(() => {
      if (!userId) {
        return
      }
      updateTimelineReadUntil()
        .then(async () => {
          await client.refetchQueries({
            include: [
              GetNewEventsCountDocument,
              MyCollaboratorsTimelineDocument,
            ],
          })
        })
        .catch((err) => {
          console.error('Failed to update timeline read until:', err)
          Sentry.captureException(err)
        })
    }, [updateTimelineReadUntil, client, userId])
  )

  const handleRefresh = useCallback(async () => {
    await refetch()
  }, [refetch])

  const handleCollaboratorSelect = useCallback(
    (item: { id: string | number; label: string }) => {
      setSelectedUserId(item.id === '' ? null : (item.id as string))
    },
    []
  )

  const handleCollaboratorSearch = useCallback((query: string) => {
    setCollaboratorSearch(query)
  }, [])

  const handleLoadMore = useCallback(() => {
    if (results.length >= total || loading || results.length === 0) return
    const nextOffset = results.length
    fetchMore({
      variables: {
        pagination: {
          offset: nextOffset,
          limit: TIMELINE_PAGE_SIZE,
        },
        userId: selectedUserId || undefined,
        actions: actionsForQuery,
        nodeId: selectedNodeId || undefined,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.myCollaboratorsEvents) return prev
        const prevResults = prev.myCollaboratorsEvents.results
        const nextResults = fetchMoreResult.myCollaboratorsEvents.results
        return {
          ...prev,
          myCollaboratorsEvents: {
            ...prev.myCollaboratorsEvents,
            total: fetchMoreResult.myCollaboratorsEvents.total,
            results: [...prevResults, ...nextResults],
          },
        }
      },
    })
  }, [
    results.length,
    total,
    loading,
    fetchMore,
    selectedUserId,
    actionsForQuery,
    selectedNodeId,
  ])

  return (
    <TimelineList
      results={results}
      total={total}
      loading={loading}
      onRefresh={handleRefresh}
      onLoadMore={handleLoadMore}
      showUserFilter
      userFilterItems={collaboratorItems}
      selectedUserId={selectedUserId}
      onUserFilterSelect={handleCollaboratorSelect}
      onUserFilterSearch={handleCollaboratorSearch}
      selectedAction={selectedAction}
      onActionFilterSelect={setSelectedAction}
      showNode={true}
      selectedNodeId={selectedNodeId}
      onNodeFilterSelect={setSelectedNodeId}
      emptyMessage={t('nodes:empty-item', 'No events yet')}
      collaboratorsUnreadMarkers
    />
  )
}

export default MyCollaboratorsUpdatesTab
